import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../../backend/convex/_generated/api';
import type { Doc } from '../../../../../backend/convex/_generated/dataModel';
import { WorkspaceAdminLayout } from '../../components/WorkspaceAdminLayout';
import { useWorkspace } from '../../hooks/useWorkspace';
import { AlertModal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';

export const MembersManagement: React.FC = () => {
  const { workspace } = useWorkspace();
  const members = useQuery(api.members.listWorkspaceMembers, 
    workspace ? { workspaceId: workspace._id } : "skip"
  );
  
  useEffect(() => {
    document.title = 'LibreRank - Members';
  }, []);

  const updateRole = useMutation(api.members.updateWorkspaceMemberRole);
  const removeMember = useMutation(api.members.removeWorkspaceMember);

  // Modal state
  const [alert, setAlert] = useState<{ isOpen: boolean; title: string; message: string; type: 'error' | 'info' }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const [confirmData, setConfirmData] = useState<{ isOpen: boolean; userId: any; userName: string }>({
    isOpen: false,
    userId: null,
    userName: ''
  });

  const handleRoleChange = async (userId: any, newRole: "admin" | "moderator" | "member") => {
    if (workspace) {
      try {
        await updateRole({ workspaceId: workspace._id, userId, role: newRole });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
        if (errorMessage.includes("Forbidden")) {
          setAlert({
            isOpen: true,
            title: 'Insufficient Permissions',
            message: 'Only workspace Admins can change member roles.',
            type: 'error'
          });
        } else {
          setAlert({
            isOpen: true,
            title: 'Update Failed',
            message: errorMessage,
            type: 'error'
          });
        }
      }
    }
  };

  const handleRemoveClick = (userId: any, userName: string) => {
    setConfirmData({ isOpen: true, userId, userName });
  };

  const handleConfirmRemove = async () => {
    if (workspace && confirmData.userId) {
      try {
        await removeMember({ workspaceId: workspace._id, userId: confirmData.userId });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
        if (errorMessage.includes("Forbidden")) {
          setAlert({
            isOpen: true,
            title: 'Insufficient Permissions',
            message: 'Only workspace Admins can remove members.',
            type: 'error'
          });
        } else {
          setAlert({
            isOpen: true,
            title: 'Removal Failed',
            message: errorMessage,
            type: 'error'
          });
        }
      } finally {
        setConfirmData({ isOpen: false, userId: null, userName: '' });
      }
    }
  };

  return (
    <WorkspaceAdminLayout title="Members Management">
      <AlertModal 
        isOpen={alert.isOpen} 
        onClose={() => setAlert({ ...alert, isOpen: false })} 
        title={alert.title} 
        message={alert.message} 
        type={alert.type} 
      />
      
      <ConfirmModal 
        isOpen={confirmData.isOpen}
        title="Remove Member"
        message={`Are you sure you want to remove ${confirmData.userName} from the workspace?`}
        confirmText="Remove"
        onConfirm={handleConfirmRemove}
        onCancel={() => setConfirmData({ isOpen: false, userId: null, userName: '' })}
      />

      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
        {!members ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading members...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}>
                <th style={{ padding: '1rem' }}>User</th>
                <th style={{ padding: '1rem' }}>Role</th>
                <th style={{ padding: '1rem' }}>Joined</th>
                <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member: Doc<"workspace_members"> & { user: Doc<"users"> | null }) => (
                <tr key={member._id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: 500 }}>{member.user?.name || 'Unknown User'}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--muted-foreground)' }}>{member.user?.email}</div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <select 
                      value={member.role}
                      onChange={(e) => handleRoleChange(member.userId, e.target.value as any)}
                      disabled={member.role === 'admin' && workspace?.role !== 'admin'}
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: '1px solid var(--border)',
                        background: 'var(--background)',
                        color: 'var(--foreground)'
                      }}
                    >
                      <option value="member">Member</option>
                      <option value="moderator">Moderator</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td style={{ padding: '1rem', color: 'var(--muted-foreground)', fontSize: '0.9rem' }}>
                    {new Date(member.joinedAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right' }}>
                    <button 
                      onClick={() => handleRemoveClick(member.userId, member.user?.name || 'User')}
                      disabled={member.role === 'admin'}
                      style={{
                        padding: '0.25rem 0.75rem',
                        color: 'var(--destructive)',
                        background: 'transparent',
                        border: '1px solid var(--destructive)',
                        borderRadius: '4px',
                        cursor: member.role === 'admin' ? 'not-allowed' : 'pointer',
                        fontSize: '0.8rem',
                        opacity: member.role === 'admin' ? 0.5 : 1
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </WorkspaceAdminLayout>
  );
};

